// services/translations.ts - Type definitions and helpers
export interface TranslationStructure {
    audioLesson: {play:string;pause:string;seek:string;missing:string;error:string;time:string}
    labCourse: {continue: string;tryIt: string;realWorld: string;stops: string;why: string;lessonCount: string;itemCount: string;done: string;nextModule: string;comingSoon: string;passed: string;notYet: string;score: string;passNote: string;failNote: string;finish: string;retry: string;leaveLine: string;minutesLeft: string;minutesLeftOne: string;lightMode: string;darkMode: string;stayButton: string;leaveAnyway: string;dragStep: string;checkRight: string;checkWrong: string;inLife: string;inTheProgram: string}
    labProgress: {close: string; saving: string; saveFailed: string; retry: string}
    nav: {
        home: string
        learn: string
        profile: string
    }
    login: {
        common: {
            google: string
            or: string
        }
        justStartingOut: string
        title: string
        emailPlaceholder: string
        passwordPlaceholder: string
        forgotPassword: string
        submitButton: string
        signUp: string
        signingIn: string
        forgotPasswordPrompt: string
        forgotEmailPlaceholder: string
        sendLink: string
        cancel: string
        resetSuccess: {
            title: string
            body: string
            backToLogin: string
            spam: string
        }
        errors: {
            invalidEmail: string
            passwordRequired: string
            emailNotFound: string
            oauthOnly: string
            wrongPassword: string
            verifyEmail: string
            oauthFailed: string
            resetInvalidEmail: string
            resetEmailNotFound: string
            resetFailed: string
        }
    }
    signup: {
        common: {
            google: string
            or: string
        }
        alreadyLearning: string
        error: string
        title: string
        firstNamePlaceholder: string
        lastNamePlaceholder: string
        emailPlaceholder: string
        passwordPlaceholder: string
        submitButton: string
        logIn: string
        strength: {
            weak: string
            fair: string
            good: string
            strong: string
            rules: {
                length: string
                uppercase: string
                number: string
                special: string
            }
            met: string
        }
        req: {
            length: string
            uppercase: string
            number: string
            special: string
        }
        errors: {
            invalidEmail: string
            emailExists: string
            weakPassword: string
            noAccountFromLogin: string
        }
        heading: string
        stepIndicator: string
        credentialsHeading: string
        continueButton: string
        back: string
    }
    classesPage: {
        yourClasses: string
        common: {
            dashboard: string
        }
        title: string
        // ── Journey-design catalog ──────────────────────────────────────────
        // OPTIONAL on purpose: these are new English strings awaiting Arabic and Hausa,
        // which are independent voices rather than translations (see the `copy` skill).
        // All three languages now carry these, so they are required — a missing one is
        // a compile error rather than a silent English fallback on a public page.
        // `freeLabel` stays optional: EN only so far.
        heading: string
        subheading: string
        allClasses: string
        clearFilters: string
        empty: string
        categoryEmpty: string
        metaTitle: string
        metaDescription: string
        categoryFilterLabel: string
        allSubjects: string
        resultsAnnouncement: string
        progressLabel: string
        categories: {
            QURAN: { label: string; blurb: string; line: string }
            ISLAMIC_SCIENCES: { label: string; blurb: string; line: string }
            TECHNOLOGY: { label: string; blurb: string; line: string }
            DESIGN: { label: string; blurb: string; line: string }
            LANGUAGE: { label: string; blurb: string; line: string }
            BUSINESS: { label: string; blurb: string; line: string }
            SKILLS_ACQUISITION: { label: string; blurb: string; line: string }
            PERSONAL_DEVELOPMENT: { label: string; blurb: string; line: string }
            HISTORY: { label: string; blurb: string; line: string }
            unfiled: { label: string; blurb: string }
        }
        badgeNew: string
        badgeSoon: string
        freeLabel?: string
        resultsHeading: string
        resultsCount: string
        resultsCountOne: string
        searchPlaceholder: string
        allLanguages: string
        languageFilterLabel: string
        courses: string
        course: string
        noResults: string
        clearSearch: string
        waitlist: {
            headline: string
            sub: string
            countLabel: string
            placeholder: string
            button: string
            successHeadline: string
            successSub: string
            errorInvalid: string
            errorGeneric: string
        }
        notifyMe: {
            button: string
            emailPlaceholder: string
            submit: string
            success: string
            errorInvalid: string
            errorGeneric: string
        }
        audienceLabels: {
            YOUNG_LEARNERS: string
            YOUTH: string
            EVERYONE: string
            MEN: string
            WOMEN: string
        }
    }
    classDetail: {
        courseLabel: string
        goToLesson: string
        lessonCount: string
        lessonCount_zero?: string
        lessonCount_one: string
        lessonCount_two?: string
        lessonCount_few?: string
        lessonCount_many?: string
        lessonCount_other: string
        upcomingLesson: string
        reloadClass: string
        pageTitle: string
        courseEmpty: string
        reviewClass: string
        classComplete: string
        currentLesson: string
        paymentLocked: string
        availableLater: string
        savedProgress: string
        progressLabel: string
        refreshFailed: string
        lessonTypes: { video: string; audio:string; quiz: string; practice: string; lesson: string; minutes: string }
        common: {
            dashboard: string
        }
        courseComplete: string
        overview: {
            duration: string
            language: string
            students: string
            learningOutcomes: string
            prerequisites: string
            curriculum: string
            tabs: string
            languageNames: {
                en: string
                ar: string
                ha: string
            }
        }
        enrolled: {
            progress: string
            stageIndicator: string
            continueLearning: string
            openClass: string
            backToDashboard: string
            expiryWarning: string
        }
        access: {
            title: string
            body: string
            fullAccessTitle: string
            fullAccessBody: string
            subscribedBody: string
            freeOutcome: string
            fullAccessLine: string
        }
        actions: {
            browseClasses: string
            startLearning: string
            startFree: string
            enroll: string
            getFullAccess: string
        }
        errors: {
            notFound: string
            enrollFailed: string
        }
    }
    landingPage: {
        heroTagline: string
        getStarted: string
        signIn: string
        browseClasses: string
        finalCTALine1: string
        finalCTALine2: string
        finalCTABody: string
        missionStatement: string
        institutionName: string
        learnAbout: string
        returnToLearning: string
        subjectsStatement: string
        subjectsHeadline: string
        cat: {
            scienceTech: string
            recitation: string
            memorization: string
            hausa: string
            more: string
        }
        pillarsTitle: string
        stand: {
            eyebrow: string
            title: string
            body: string
        }
        method: {
            bitesized: { eyebrow: string; title: string; body: string }
            depth: { eyebrow: string; title: string; body: string }
            thinking: { eyebrow: string; title: string; body: string }
        }
        assurance: {
            eyebrow: string
            title: string
            body: string
        }
        pillar0Title: string
        pillar1Title: string
        pillarLanguagesTitle: string
        pillarHadithTitle: string
        pillarFiqhTitle: string
        pillarTafsirTitle: string
        pillarAqidahTitle: string
        pillarSeerahTitle: string
        pillarArabicTitle: string
        pillarTajweedTitle: string
        pillarUsulTitle: string
        pillarNahwTitle: string
        pillarEnglishTitle: string
        pillarMathTitle: string
        statusActive: string
        statusComingSoon: string
        learning: {
            title: string
            body: string
            step1: {
                title: string
                body: string
            }
            step2: {
                title: string
                body: string
            }
            step3: {
                title: string
                body: string
            }
        }
        trust: {
            title: string
            body: string
            students: {
                title: string
                body: string
            }
            family: {
                title: string
                body: string
            }
            returning: {
                title: string
                body: string
            }
            teachers: {
                title: string
                body: string
            }
        }
        classes: string
        about: string
        privacy: string
        subjects: {
            mathematics: string
            engineering: string
            sciences: string
            technology: string
            computerScience: string
            ai: string
            arts: string
        }
        seo: {
            title: string
            description: string
        }
        aria: {
            goToHomepage: string
            skipToContent: string
            subjectsPrev: string
            subjectsNext: string
        }
    }
    errorPage: {
        tryAgain: string
        inlineMessage: string
        goBack: string
        offline: {
            title: string
            body: string
            retry: string
            backOnline: string
        }
        serverError: {
            title: string
            body: string
            retry: string
            goHome: string
        }
        notFound: {
            title: string
            body: string
            action: string
            goHome: string
            goBack: string
        }
        sessionExpired: {
            title: string
            body: string
            action: string
        }
        forbidden: { title: string; body: string }
    }
    aboutPage: {
        eyebrow: string
        heroTitle: string
        belief1: string
        belief2: string
        coda2: string
    }
    footer: {
        tagline: string
        learnHeading: string
        legalHeading: string
        navClasses: string
        navAbout: string
        navDashboard: string
    }
    classCard: {
        comingSoon: string
        hiddenBadge: string
        start: string
        continue: string
        review: string
        viewClass: string
    }
    enrollmentSuccess: {
        heading: string
        startLearning: string
        backToClass: string
        stageUnlocked: string
    }
    paymentCallback: {
        missingTitle: string
        missingBody: string
        signinTitle: string
        signinBody: string
        verifyingTitle: string
        verifyingBody: string
        successTitle: string
        successBody: string
        successBodyApp: string
        returningToApp: string
        openApp: string
        continueOnWeb: string
        successGuideTitle: string
        successWeb: string
        successEmail: string
        successAndroidBeta: string
        failedTitle: string
        failedBody: string
        tryAgain: string
        continue: string
        confirmingDelayTitle: string
        confirmingDelayBody: string
        retry: string
        backToDashboard: string
    }
    unsubscribe: {
        loadingTitle: string
        doneTitle: string
        doneBody: string
        invalidTitle: string
        invalidBody: string
        backHome: string
    }
    appHandoff: {
        missingTitle: string
        missingBody: string
        desktopBody: string
        title: string
        body: string
        openApp: string
        continueOnWeb: string
    }
    learningRecords: {
        retry: string
        loadError: string
        noCertificates: string
        earnCertificate: string
        viewClasses: string
        viewCertificate: string
        history: string
        historyBody: string
        todayDone: string
        todayPending: string
        partial: string
        learned: string
        today: string
        upcoming: string
        noActivity: string
        unavailable: string
        hours: string
        minutes: string
        last12Months: string
        noHistory: string
        certificate: string
        verifyTitle: string
        verifyError: string
        verifyErrorBody: string
        shareFailed: string
        linkLabel: string
        loading: string
        verifyDescription: string
    }
    analyticsPage: {
        back: string
        title: string
        subtitle: string
        todayDone: string
        todayPending: string
        daysShownUp: string
        dayShownUp: string
        longestRun: string
        lessonsLifetime: string
        timeLifetime: string
        yourYear: string
        yourRecord: string
        roomToGrow: string
        reminderTitle: string
        reminderBody: string
        reminderCta: string
        freeTeaserTitle: string
        freeTeaserBody: string
        freeUpgradeCta: string
        discoveryTitle: string
        discoverySubtitle: string
        discoveryCta: string
        discoveryCtaUpgrade: string
        premiumWallTitle: string
        premiumWallBody: string
        premiumWallCta: string
    }
    dashboard: {
        welcomeBack: string
        readyToStart: string
        discoverClasses: string
        findClasses: string
        keepGoingHeading: string
        chooseClass: string
        findClass: string
        nextLessonUnavailable: string
        learningHeading: string
        classShelfHeading: string
        weekSummary: string
        learningDays: string
        dayStreak: string
        minutesSoFar: string
        continueLearning: string
        progress: string
        browseClasses: string
        review: string
        today: string
        daysActiveThisMonth: string
        lessonsCompleted: string
        keepGoing: string
        pickUpWhereYouLeftOff: string
        stage: string
        thisWeek: string
        continueCourse: string
        previousClass: string
        nextClass: string
        nextUp: string
        viewRoadmap: string
        momentum: string
        yourProgress: string
        seeFullHistory: string
        todayDone: string
        todayNudge: string
        administration: string
        adminDashboard: string
        learnerFallback: string
        scrollLeft: string
        scrollRight: string
        courses: string
        days: {
            sun: string
            mon: string
            tue: string
            wed: string
            thu: string
            fri: string
            sat: string
        }
        yourCertificates: string
        viewCertificate: string
        analytics: {
            lessons: string
            time: string
            daysActive: string
            monthlyProgress: string
            yearlyProgress: string
            unlockHistory: string
            activityLess: string
            activityMore: string
            prevYear: string
            nextYear: string
            loadError: string
        }
        freeBanner: {
            text: string
            cta: string
        }
    }
    moduleSelection: {
        accountSettings: string
        signOut: string
        startLearning: string
        startModule: string
        continueModule: string
        minutes: string
        hours: string
    }
    auth: {
        signingIn: string
        pleaseWait: string
    }
    payment: {
        retry: string
        error: string
        errorBody: string
        preparing: string
        preparingSub: string
        backToPricing: string
    }
    classOverview: {
        common: {
            dashboard: string
        }
        locked: string
        minutes: string
        yourJourney: string
        courses: string
        nextChapterAwaits: string
        unlockNextStage: string
        continueJourney: string
        stageLabel: string
        complete: string
        learners: string
        stages: string
        classComplete: string
        classCompleteBody: string
        upToDate: string
        upToDateBody: string
        comingSoon: string
        reviewNeeded: string
        continueHint: string
        reviewLessonsCount: string
        reviewNow: string
        lockedHint: string
    }
    classroom: {
        checkAnswer: string
        nextQuestion: string
        submitAnswer: string
        continue: string
        completeQuiz: string
        loading: string
        exitLesson: string
        lessonNotFound: string
        returnCourse: string
        connectionError: string
        connectionErrorDesc: string
        questionOfTotal: string
        selectAll: string
        videoNotAvailable: string
        noQuestions: string
        practiceNotConfigured: string
        typeAnswer: string
        correct: string
        incorrect: string
        tryAgain: string
        review: string
        reviewComplete: string
        reviewCompleteSubtitle: string
        reviewCompleteContext: string
        reviewContinue: string
        lessonNotConfigured: string
        lessonSlides: string
        premiumOnly: string
        tapToRetry: string
        lessonResources: string
        downloadResource: string
        premiumResourceLocked: string
        masterySaveError: string
        hearIt: string
        muteSounds: string
        unmuteSounds: string
        recite: {
            compare: string
            record: string
            recordAgain: string
            stop: string
            yours: string
            listenReference: string
            recitedAloud: string
            micDenied: string
            recordFirst: string
        }
        explain: {
            reveal: string
            model: string
            placeholder: string
        }
        selfCheck: {
            title: string
            practicing: string
            gotIt: string
        }
        trace: {
            instruction: string
            clear: string
            done: string
        }
        // Optional so ar/ha can omit these until /copy authors them (the en
        // fallback supplies the text at runtime — no English placeholder in ar/ha).
        step?: {
            next: string
            done: string
        }
        letters: {
            tapToHear: string
        }
        instruction: {
            fillGap: string
            explain: string
        }
        code: {
            run: string
            format: string
            running: string
            preparing: string
            preparingNote: string
            allPassed: string
            someFailed: string
            test: string
            expected: string
            got: string
            pythonError: string
            output: string
            reset: string
            copy: string
            copied: string
        }
        level: {
            beginner: string
            intermediate: string
            advanced: string
        }
        exitModal: {
            title: string
            stay: string
            leave: string
        }
        why: string
        whyModal: {
            title: string
            gotIt: string
        }
        speak: {
            reveal: string
            hint: string
        }
        glossary: {
            hint: string
        }
        recap: {
            title: string
        }
        flashcard: {
            front: string
            back: string
            tapToFlip: string
            lockHint: string
            gotIt: string
        }
        estimate: {
            reveal: string
            hint: string
        }
        terminal: {
            placeholder: string
            hint: string
        }
        checkpoint: {
            submit: string
            submitted: string
        }
        parsons: {
            empty: string
            remove: string
            hint: string
            program: string
            blocks: string
            allPlaced: string
        }
        connect: {
            step: string
            hint: string
        }
        hotspot: {
            hint: string
            wrong: string
        }
        branch: {
            tryAnother: string
            good: string
            bad: string
        }
        dialogue: {
            wrong: string
        }
    }
    settings: {
        saveError: string
        deleteError: string
        sessionsLoadError: string
        sessionsActionError: string
        unknownDevice: string
        computer: string
        retry: string
        firstName: string
        lastName: string
        accountSettings: string
        subtitle: string
        backToDashboard: string
        profile: string
        preferences: string
        preferencesDesc: string
        sections: {
            notifications: string
            notificationsDesc: string
            membership: string
            security: string
            securityDesc: string
        }
        membership: {
            fullAccess: string
            freePlan: string
            everyStageUnlocked: string
            renewsOn: string
            freeBody: string
            upgradeCta: string
            coveredUntil: string
            autoChargeNote: string
            renewCta: string
        }
        dataManagement: string
        interfaceLanguage: string
        courseNotifications: string
        notificationsDesc: string
        autoPlayVideos: string
        autoPlayDesc: string
        sounds: string
        soundsDesc: string
        exportData: string
        exporting: string
        exportError: string
        exportDesc: string
        deleteDesc: string
        saveChanges: string
        cancel: string
        signOut: string
        activeSessions: string
        sessionsTab: string
        sessionsDescription: string
        currentDevice: string
        signOutDevice: string
        signOutAllOtherDevices: string
        noActiveSessions: string
        lastActive: string
        saving: string
        saved: string
        whatsapp: {
            heading: string
            phone: string
            phoneHint: string
            optIn: string
            save: string
            helper: string
            saveError: string
        }
        emailFieldLabel: string
        emailCannotChange: string
        addPasswordTitle: string
        addPasswordDesc: string
        addPasswordCta: string
        newPassword: string
        confirmPassword: string
        passwordPlaceholder: string
        confirmPlaceholder: string
        savePassword: string
        passwordSetSuccess: string
        passwordSetFail: string
        networkError: string
        passwordTooShort: string
        passwordMismatch: string
        theme: string
        themeDark: string
        themeLight: string
        themeSystem: string
        themeDescSystem: string
        themeDescDark: string
        themeDescLight: string
        languageAutoSave: string
        exportTitle: string
        exportComingSoon: string
        deleteTitle: string
        deleting: string
        deleteConfirmTitle: string
        deleteConfirmMsg: string
        deleteFinalTitle: string
        deleteFinalMsg: string
        deleteBtnLabel: string
        firstNamePlaceholder: string
        lastNamePlaceholder: string
        continue: string
    }
    expiry: {
        title: string
        body: string
        subscribeCta: string
        dismissCta: string
    }
    expiryBanner: {
        message: string
        messageToday: string
        renewCta: string
    }
    subscriptionModal: {
        title: string
        subtitle: string
        cta: string
    }
    paywall: {
        paymentValidationError: string
        yearlySaving: string
        signupFirst: string
        paymentError: string
        eyebrow: string
        headlineLocked: string
        headlineExpired: string
        headlineDefault: string
        headlineRenew: string
        lead: string
        leadExpired: string
        leadRenew: string
        continue: string
        maybeLater: string
        back: string
        close: string
        perMonthBilledYearly: string
        honestBilling: string
        promise: string
        periodMonth: string
        periodYear: string
        noCommitment: string
        noAutoCharge: string
        secure: string
        goBack: string
        offerTitle: string
        offerBody: string
        offerStartMonthly: string
        offerDecline: string
    }
    door: {
        winEyebrow: string
        winHeadline: string
        winBody: string
        winPrimary: string
        moreEyebrow: string
        moreHeadline: string
        moreBody: string
        morePrimary: string
        priceLine: string
        notNow: string
        saveEyebrow: string
        saveHeadline: string
        saveBody: string
        savePrimary: string
        namePlaceholder: string
        fillAll: string
        error: string
    }
    onboarding: {
        title1: string
        sub1: string
        title2: string
        sub2: string
        levelNew: string
        levelSome: string
        levelAdvanced: string
        back: string
        lessonLoading: string
    }
    verifyNudge: {
        body: string
        resend: string
        sent: string
        dismiss: string
    }
    completionCard: {
        premiumTeaser: string
        unlockCta: string
    }
    lessonComplete: {
        eyebrow: string
        continue: string
    }
    moduleComplete: {
        label: string
        continue: string
        headline: string
        headlineLast: string
        position: string
        nextModule: string
        finishCourse: string
    }
    courseComplete: {
        label: string
        title: string
        coursesInStage: string
        nextCourse: string
        backToClass: string
        subscribeCta: string
        startReview: string
        questionsToReview: string
        upgradeHint: string
        upgradeCta: string
        checkBackSoon: string
        continue: string
        subtitle: string
        reviewMistake: string
        reviewMistakes: string
        youKnow: string
        statModule: string
        statModules: string
        statLesson: string
        statLessons: string
        statMastery: string
        mastery: {
            title: string
            masteryScore: string
            accuracy: string
            lessonsMastered: string
            of: string
            loadError: string
            noAttempts: string
            improved: string
            sameBest: string
            stillBest: string
            before: string
            now: string
        }
    }
    stageComplete: {
        eyebrow: string
        nextStage: string
        subtitle: string
        beginStage: string
        upgradeTitle: string
        upgradeBody: string
        upgradeCta: string
        upgradeInvite: string
    }
    lessonComingSoon: {
        titleLesson: string
        titleCourse: string
        bodyLesson: string
        bodyCourse: string
        backToClass: string
    }
    pricingPage: {
        eyebrow: string
        title: string
        subtitle: string
        primaryCardTitle: string
        primaryLead: string
        billingLabel: string
        includedTitle: string
        freeTitle: string
        freeBody: string
        freePointStage: string
        freePointPreview: string
        freePointNoCard: string
        afterTitle: string
        afterPay: string
        afterClass: string
        afterMobile: string
        secureTitle: string
        footerNote: string
        socialProof: string
        featuresLabel: string
        aria: {
            close: string
        }
        goToDashboard: string
        toggle: {
            monthly: string
            yearly: string
            saveBadge: string
        }
        period: {
            month: string
            year: string
        }
        free: {
            name: string
            period: string
            subtitle: string
            cta: string
        }
        fullAccess: {
            name: string
            cta: string
            badge: string
            yearlySaving: string
            monthlyNote: string
        }
        feature: {
            stage1: string
            videosQuizzes: string
            allStages: string
            studyMat: string
            certificate: string
            everythingInFree: string
            webMobile: string
        }
    }
    verifyEmail: {
        title: string
        subtitle: string
        sentTo: string
        sentGeneric: string
        instructions: string
        verifying: string
        verifyingBody: string
        successTitle: string
        successBody: string
        successCta: string
        errorTitle: string
        resendPrompt: string
        didntReceive: string
        emailPlaceholder: string
        resendButton: string
        resentTitle: string
        resentConfirm: string
        backToLogin: string
        errors: {
            emailRequired: string
            failed: string
            resendFailed: string
        }
    }
    forgotPassword: {
        title: string
        subtitle: string
        emailPlaceholder: string
        submitButton: string
        sentTitle: string
        sentBody: string
        sentHint: string
        errors: {
            invalidEmail: string
            sendFailed: string
        }
    }
    resetPassword: {
        title: string
        subtitle: string
        newPasswordPlaceholder: string
        confirmPasswordPlaceholder: string
        submitButton: string
        successTitle: string
        successBody: string
        successCta: string
        expiredTitle: string
        expiredBody: string
        newLinkCta: string
        req: {
            length: string
            uppercase: string
            number: string
            special: string
        }
        errors: {
            noToken: string
            mismatch: string
            weak: string
            failed: string
        }
    }
    achievements: {
        title: string
        subtitle: string
        verified: string
        noCertificates: string
        premiumRequired: string
        downloading: string
    }
    // NOTE: the certificate DOCUMENT itself is fixed-English (a document of
    // record — see CertificateDocument.tsx); these keys cover only the page
    // chrome around it.
    certificate: {
        title: string
        certNumberLabel: string
        download: string
        downloading: string
        downloadFailed: string
        notFound: string
        preparing: string
        share: string
        shareCopied: string
        verifyValid: string
        verifyInvalid: string
        verifyInvalidMessage: string
        verifyReturnHome: string
        verifyIssuedBy: string
        verifiableAt: string
        holderLabel: string
        classLabel: string
        completedLabel: string
    }
    classComplete: {
        title: string
        viewCertificate: string
        viewCertificates: string
        browseClasses: string
        premiumOnly: string
        upgradeForCertificate: string
        unlockCertificate: string
        subtitlePremium: string
        subtitlePreparing: string
        subtitleFree: string
        claimLine: string
        claimCta: string
        welcomeHome: string
        seeCertificate: string
        yoursToKeep: string
        nextJourney: string
        openCertificate: string
        completedTheClass: string
        skip: string
        courseCount: string
        courseCount_zero?: string
        courseCount_one: string
        courseCount_two?: string
        courseCount_few?: string
        courseCount_many?: string
        courseCount_other: string
        lessonCount: string
        lessonCount_zero?: string
        lessonCount_one: string
        lessonCount_two?: string
        lessonCount_few?: string
        lessonCount_many?: string
        lessonCount_other: string
    }
    welcome: {
        intro: {
            sentence: string
        }
        skip: string
        step1: {
            title: string
            youngLearner: string
            youth: string
            men: string
            women: string
            everyone: string
        }
        step2: {
            title: string
            subtitle: string
            islamic_sciences: string
            quran: string
            languages: string
            stem: string
            all: string
            continue: string
            createAccount: string
            browseClasses: string
        }
        step3: {
            title: string
            monthly: string
            yearly: string
            savePercent: string
            mostPopular: string
            browseFree: string
            getBasic: string
            getPremium: string
        }
    }
    deleteAccountPage: {
        title: string
        headline: string
        intro: string
        signedInTitle: string
        signedInBody: string
        openSettings: string
        emailTitle: string
        emailBefore: string
        emailAfter: string
        deletedTitle: string
        deletedAccount: string
        deletedProgress: string
        deletedCertificates: string
        deletedPayments: string
        nothingKept: string
    }
    app: {
        offlineWarning: string
        announcementDefaultCta: string
    }
}


// Helper function for template strings
export function translateTemplate(
    template: string,
    values: Record<string, string | number>
): string {
    return template.replace(/\{\{(\w+)\}\}/g, (_, key) => String(values[key] || ''))
}
